import { useEffect, useRef, useState } from "react";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Play, Stop, ClipboardText, Monitor, CircleNotch, ShieldCheck, Info } from "@phosphor-icons/react";

const STEPS = [
  "Enable Remote Management on the Mac (use the Deploy Scripts tab).",
  "Save the agent script below as mdmforge-agent.py on the target Mac.",
  "Run:  pip3 install websockets  (one-time).",
  "Run:  python3 mdmforge-agent.py  — the viewer connects automatically.",
];

export default function RemoteLiveSession() {
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [vncPassword, setVncPassword] = useState("");
  const screenRef = useRef(null);
  const rfbRef = useRef(null);
  const pollRef = useRef(null);
  const triedRef = useRef(false);

  const stopAll = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    if (rfbRef.current) { try { rfbRef.current.disconnect(); } catch (e) {} rfbRef.current = null; }
    setConnected(false); triedRef.current = false;
  };
  useEffect(() => () => stopAll(), []);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/rd/sessions", { name: "Mac session" });
      setSession(data); setStatus({ status: "waiting", agent_connected: false });
      pollRef.current = setInterval(() => poll(data.code), 3000);
      toast.success("Session started — deploy the agent to connect");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start session");
    } finally { setStarting(false); }
  };

  const poll = async (code) => {
    try {
      const { data } = await api.get(`/rd/sessions/${code}`);
      setStatus(data);
      if (data.status === "expired") { stopAll(); toast.message("Session expired"); }
      else if (data.agent_connected && !triedRef.current) { triedRef.current = true; connectViewer(code); }
    } catch (e) {}
  };

  const connectViewer = async (code) => {
    setConnecting(true);
    try {
      const mod = await import("@novnc/novnc/core/rfb");
      const RFB = mod.default;
      const wsUrl = API.replace(/^http/, "ws") + `/rd/viewer/${code}`;
      const rfb = new RFB(screenRef.current, wsUrl, vncPassword ? { credentials: { password: vncPassword } } : {});
      rfb.scaleViewport = true;
      rfb.addEventListener("connect", () => { setConnected(true); setConnecting(false); toast.success("Connected to Mac"); });
      rfb.addEventListener("disconnect", () => { setConnected(false); setConnecting(false); });
      rfb.addEventListener("credentialsrequired", () => {
        const pw = vncPassword || window.prompt("VNC password for this Mac:");
        if (pw) rfb.sendCredentials({ password: pw });
      });
      rfb.addEventListener("securityfailure", () => { toast.error("VNC authentication failed"); setConnecting(false); });
      rfbRef.current = rfb;
    } catch (err) {
      setConnecting(false);
      toast.error("Viewer failed to load");
    }
  };

  const end = async () => {
    if (session) { try { await api.delete(`/rd/sessions/${session.code}`); } catch (e) {} }
    stopAll(); setSession(null); setStatus(null);
    toast.message("Session ended");
  };

  const copyAgent = () => { navigator.clipboard.writeText(session?.agent_script || ""); toast.success("Agent script copied"); };

  const inputCls = "w-full bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none";
  const statusColor = connected ? "#00FF41" : status?.agent_connected ? "#0A84FF" : "#FEBC2E";
  const statusText = connected ? "Live" : connecting ? "Connecting…" : status?.agent_connected ? "Agent online" : status?.status === "expired" ? "Expired" : "Waiting for agent";

  return (
    <div className="p-8">
      <div className="border border-[#27272A] bg-[#0f0f11] p-4 mb-6 flex items-start gap-3" data-testid="rd-live-info">
        <Info size={18} className="text-[#00F0FF] mt-0.5 shrink-0" />
        <p className="text-[11px] text-[#A1A1AA] font-mono leading-relaxed">
          In-browser screen sharing (beta). The Mac runs a tiny agent that dials this relay — no inbound ports opened.
          The relay pairs your browser (noVNC) with the Mac's built-in Screen Sharing. Sessions expire automatically.
        </p>
      </div>

      {!session ? (
        <button data-testid="rd-live-start" onClick={start} disabled={starting}
          className="flex items-center gap-2 bg-[#00F0FF] text-[#0A0A0B] font-medium px-6 py-3 text-sm hover:bg-[#00d4e0] transition-colors duration-200 disabled:opacity-50">
          <Play size={16} weight="fill" /> {starting ? "Starting…" : "Start live session"}
        </button>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm text-[#FAFAFA]">Session <span className="text-[#00F0FF]">{session.code}</span></span>
                <span className="flex items-center gap-2 font-mono text-xs" style={{ color: statusColor }} data-testid="rd-live-status">
                  <span className="w-2 h-2 rounded-full" style={{ background: statusColor }} /> {statusText}
                </span>
              </div>
              <button data-testid="rd-live-end" onClick={end} className="flex items-center gap-2 border border-[#FF5F56] text-[#FF5F56] px-4 py-2 text-sm hover:bg-[#FF5F56]/10 transition-colors duration-200"><Stop size={15} weight="fill" /> End</button>
            </div>
            <div ref={screenRef} data-testid="rd-live-screen"
              className="w-full aspect-[16/10] bg-[#050505] border border-[#27272A] flex items-center justify-center overflow-hidden">
              {!connected && (
                <div className="text-center">
                  {connecting ? <CircleNotch size={28} className="text-[#00F0FF] animate-spin mx-auto mb-3" />
                    : <Monitor size={28} className="text-[#3f3f46] mx-auto mb-3" />}
                  <p className="text-xs text-[#71717A] font-mono">{connecting ? "Negotiating VNC…" : "Waiting for the Mac agent to connect…"}</p>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block font-mono text-[10px] text-[#71717A] mb-1.5 uppercase tracking-wider">VNC password (if set on the Mac)</label>
              <input data-testid="rd-live-vncpw" type="password" value={vncPassword} onChange={(e) => setVncPassword(e.target.value)} className={inputCls} placeholder="optional" />
            </div>
            <div className="border border-[#27272A] bg-[#0A0A0B] p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[10px] text-[#71717A] uppercase tracking-wider">Deploy the agent</span>
                <button data-testid="rd-live-copy-agent" onClick={copyAgent} className="flex items-center gap-1.5 text-xs text-[#00F0FF] hover:underline"><ClipboardText size={14} /> Copy script</button>
              </div>
              <ol className="space-y-2">
                {STEPS.map((s, i) => (
                  <li key={i} className="flex gap-2 text-[11px] font-mono text-[#A1A1AA] leading-relaxed">
                    <span className="text-[#00F0FF]">{i + 1}.</span> {s}
                  </li>
                ))}
              </ol>
            </div>
            <div className="border border-[#27272A] bg-[#0f0f11] p-3 flex items-start gap-2">
              <ShieldCheck size={14} className="text-[#00FF41] mt-0.5 shrink-0" />
              <p className="text-[10px] text-[#71717A] font-mono leading-relaxed">Session token is single-use &amp; expires in {session.ttl_minutes} min. Only you can view it.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
